/**
 * Expense Approval offline queue/cache primitives.
 *
 * Uses appShellStore for cache and queue persistence, and
 * offlineMutationLedger for mutation tracking (so the counts
 * appear in masterSync stats).
 */
import appShellStore from '@/lib/offline/appShellStore';
import {
  appendOfflineMutation,
  updateOfflineMutation,
} from '@/lib/offlineMutationLedger';
import { authService } from '@/lib/authService';

const CACHE_VERSION = 1;
const EXPENSE_CACHE_KEY = `expense_approval_cache:v${CACHE_VERSION}`;
const EXPENSE_QUEUE_KEY = `expense_approval_queue:v${CACHE_VERSION}`;

export interface QueuedExpenseAction {
  type: 'approve' | 'reject';
  expenseId: string;
  createBill?: boolean;
  reason?: string;
  clientId: number;
  userId: number;
  createdAt: string;
}

export interface ExpenseOfflineCache {
  expenses: Record<string, unknown>[];
  cachedAt: string;
}

function getIdentity(): { clientId: number; userId: number } | null {
  const user = authService.getUser();
  if (!user?.client_id || !user?.userId) return null;
  return { clientId: Number(user.client_id), userId: Number(user.userId) };
}

// ---------------------------------------------------------------------------
// Cache
// ---------------------------------------------------------------------------

export async function saveExpenseCache(expenses: Record<string, unknown>[]): Promise<void> {
  const identity = getIdentity();
  if (!identity) return;

  const payload: ExpenseOfflineCache = {
    expenses,
    cachedAt: new Date().toISOString(),
  };

  await appShellStore.upsertJson(identity.clientId, EXPENSE_CACHE_KEY, payload);
}

export async function loadExpenseCache(): Promise<Record<string, unknown>[] | null> {
  const identity = getIdentity();
  if (!identity) return null;

  const data = await appShellStore.getJson<ExpenseOfflineCache>(identity.clientId, EXPENSE_CACHE_KEY);
  if (!data?.expenses || !Array.isArray(data.expenses)) return null;
  return data.expenses;
}

// ---------------------------------------------------------------------------
// Queue
// ---------------------------------------------------------------------------

export async function queueExpenseAction(action: Omit<QueuedExpenseAction, 'clientId' | 'userId' | 'createdAt'>): Promise<void> {
  const identity = getIdentity();
  if (!identity) return;

  const fullAction: QueuedExpenseAction = {
    ...action,
    clientId: identity.clientId,
    userId: identity.userId,
    createdAt: new Date().toISOString(),
  };

  const existing = await appShellStore.loadSettingsArrayMigration<QueuedExpenseAction>(identity.clientId, EXPENSE_QUEUE_KEY);
  const next = [...existing, fullAction];
  await appShellStore.saveSettingsArrayMigration(identity.clientId, EXPENSE_QUEUE_KEY, next);

  // Track in mutation ledger for masterSync observability
  const dedupeKey = `expense_${action.type}_${action.expenseId}`;
  await appendOfflineMutation({
    clientId: identity.clientId,
    userId: identity.userId,
    module: 'operations',
    entityType: 'expense',
    entityId: action.expenseId,
    operationType: action.type,
    scope: 'self',
    dedupeKey,
    payload: { type: action.type, expenseId: action.expenseId, createBill: action.createBill, reason: action.reason },
    metadata: { source: 'expense_approval_board', created_at: fullAction.createdAt },
  });
}

export async function loadQueuedExpenseActions(): Promise<QueuedExpenseAction[]> {
  const identity = getIdentity();
  if (!identity) return [];

  const queue = await appShellStore.loadSettingsArrayMigration<QueuedExpenseAction>(identity.clientId, EXPENSE_QUEUE_KEY);
  return queue.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}

export async function clearExpenseQueue(): Promise<void> {
  const identity = getIdentity();
  if (!identity) return;

  await appShellStore.saveSettingsArrayMigration<QueuedExpenseAction>(identity.clientId, EXPENSE_QUEUE_KEY, []);
}

export async function setExpenseQueue(actions: QueuedExpenseAction[]): Promise<void> {
  const identity = getIdentity();
  if (!identity) return;

  await appShellStore.saveSettingsArrayMigration(identity.clientId, EXPENSE_QUEUE_KEY, actions);
}

// ---------------------------------------------------------------------------
// Replay
// ---------------------------------------------------------------------------

export async function replayQueuedExpenseActions(
  replayFn: (action: QueuedExpenseAction) => Promise<boolean>
): Promise<{ synced: number; failed: number; remaining: number }> {
  const queue = await loadQueuedExpenseActions();
  if (queue.length === 0) {
    return { synced: 0, failed: 0, remaining: 0 };
  }

  const failed: QueuedExpenseAction[] = [];
  let synced = 0;

  for (const action of queue) {
    try {
      const ok = await replayFn(action);
      if (ok) {
        synced++;
        // Mark mutation ledger entry as synced
        await updateOfflineMutation({
          clientId: action.clientId,
          dedupeKey: `expense_${action.type}_${action.expenseId}`,
          status: 'synced',
          syncedAt: new Date().toISOString(),
        });
      } else {
        failed.push(action);
      }
    } catch {
      failed.push(action);
    }
  }

  if (failed.length === 0) {
    await clearExpenseQueue();
  } else {
    await setExpenseQueue(failed);
  }

  return { synced, failed: failed.length, remaining: failed.length };
}

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

export async function getPendingExpenseActionCount(): Promise<number> {
  const queue = await loadQueuedExpenseActions();
  return queue.length;
}
